/* Where each piece of the session lives, and why.

   ── The decision ──────────────────────────────────────────────────────────────

   - **Access token: memory only.** A module-scoped variable. Never
     localStorage, never sessionStorage, never a cookie.
   - **Refresh token: sessionStorage.**
   - **PKCE `code_verifier` and `state`: sessionStorage**, because they must
     survive a full-page redirect to Cardea and back. There is no alternative —
     memory does not survive a navigation, and the whole point of `state` is that
     it is checked *after* the browser has left and returned.
   - **Display name: sessionStorage**, and it is not a credential — it is the
     string in the header. It lives there only so a reload does not render a
     blank user pill while the refresh is in flight.

   ── Why the access token stays off disk ───────────────────────────────────────

   The access token is the credential presented on every API request; it is the
   thing worth stealing. Keeping it in memory means a script that sweeps
   `localStorage`/`sessionStorage` at load — the cheap, generic attack, and the
   one a supply-chained dependency runs first — finds nothing. It also means the
   token is gone the instant the tab closes or the process ends, with no
   cleanup step that can be skipped or fail.

   ── Why the refresh token is not in memory ────────────────────────────────────

   Because it has to survive a reload. If it did not, every F5 would bounce the
   user out to Cardea and back, and Milo would be unusable. `sessionStorage`
   rather than `localStorage` bounds how long a compromise persists: one tab, one
   browsing session, cleared when the tab closes. Cardea's refresh tokens live
   **30 days** — that is a long-lived credential to leave sitting on disk in
   `localStorage`, readable by every script on this origin, for a month.

   ── The honest limit ─────────────────────────────────────────────────────────

   None of this stops XSS. A script executing on this origin can read
   `sessionStorage`, and it can equally well monkey-patch `fetch` or
   `Object.defineProperty` its way to an in-memory token on the next request.
   **The storage choice bounds the persistence of a compromise, not its
   existence.** Claiming otherwise would be the dangerous part.

   The actual fix is a backend-for-frontend: the server holds both tokens, the
   browser holds an `HttpOnly`, `Secure`, `SameSite` cookie it cannot read, and
   an XSS can then only *act as* the user while the page is open rather than
   walk away with a 30-day credential. That is a real backend change — a session
   store, a cookie-authenticated proxy for every API route, CSRF defence — and
   is deliberately deferred, not overlooked.

   ── What this costs, concretely ──────────────────────────────────────────────

   - A page reload costs one refresh round-trip to Cardea before the first API
     call can go out.
   - Closing the tab signs you out. Opening Milo in a second tab is a second
     sign-in.
   Both are accepted. */

/** Namespaced so nothing else on the origin collides with us. */
const REFRESH_KEY = 'milo.auth.refresh_token'
const PENDING_KEY = 'milo.auth.pending'
const USER_KEY = 'milo.auth.user'

export interface AccessToken {
  value: string
  /** Epoch milliseconds. Derived from `expires_in` at receipt — never from `iat`. */
  expiresAt: number
}

/** The in-flight half of an authorization, held across the redirect to Cardea. */
export interface PendingAuthorization {
  state: string
  codeVerifier: string
  nonce: string
}

/* The access token. Module scope, deliberately — see the block above. */
let accessToken: AccessToken | null = null

export function readAccessToken(): AccessToken | null {
  return accessToken
}

export function writeAccessToken(token: AccessToken | null): void {
  accessToken = token
}

/* sessionStorage can throw rather than merely be empty: Safari in private
   browsing has historically thrown on write, and an embedded context can have
   storage blocked outright. Everything below degrades to "no stored value",
   which downgrades the app to a sign-in per page load rather than breaking it. */

function readItem(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeItem(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch {
    /* Storage unavailable. The session simply will not survive a reload. */
  }
}

export function readRefreshToken(): string | null {
  return readItem(REFRESH_KEY)
}

export function writeRefreshToken(token: string | null): void {
  writeItem(REFRESH_KEY, token)
}

export function readUserName(): string | null {
  return readItem(USER_KEY)
}

export function writeUserName(name: string | null): void {
  writeItem(USER_KEY, name)
}

export function writePending(pending: PendingAuthorization): void {
  writeItem(PENDING_KEY, JSON.stringify(pending))
}

/**
 * Read the pending authorization **and delete it in the same breath**.
 *
 * One-time by construction: `state` and `code_verifier` are single-use, and
 * leaving them behind after a failed callback would let a second attempt reuse
 * a verifier whose code has already been redeemed. Callers get one shot,
 * whether the exchange goes on to succeed or fail.
 */
export function takePending(): PendingAuthorization | null {
  const raw = readItem(PENDING_KEY)
  writeItem(PENDING_KEY, null)
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as PendingAuthorization).state === 'string' &&
      typeof (parsed as PendingAuthorization).codeVerifier === 'string' &&
      // `nonce` too: session.ts compares it against the ID token's claim, and a
      // record missing it would silently skip that check rather than fail it.
      typeof (parsed as PendingAuthorization).nonce === 'string'
    ) {
      return parsed as PendingAuthorization
    }
    return null
  } catch {
    return null
  }
}

/** Forget everything: the in-memory token and every stored key we own. */
export function clearAll(): void {
  accessToken = null
  writeItem(REFRESH_KEY, null)
  writeItem(PENDING_KEY, null)
  writeItem(USER_KEY, null)
}
